﻿/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import { NotificationBell } from "@/components/notifications/NotificationBell";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { Outlet, useRouterState } from "@tanstack/react-router";
import { ArrowRightStartOnRectangleIcon as LogoutIcon } from "@heroicons/react/24/outline";
import { Bars3Icon } from "@heroicons/react/24/outline";
import { initializeDarkMode } from "@/utils/darkMode";
import { getWallpaper, Wallpaper } from "@/utils/wallpaper";
import { WALLPAPER_PRESETS } from "@/constants/wallpaperPresets";
import { useAuth } from "@/context/auth";
import { useTabNavigationContext } from "@/context/tabNavigation";
import { TabNavigation } from "@/components/common/TabNavigation";
import { Sidebar } from "@/components/common/Sidebar";
import { BrandMark } from "@/components/common/BrandMark";
import { TopInfo } from "@/components/common/TopInfo";
import { CommandPalette } from "@/components/CommandPalette";
import { StyleToggle } from "@/components/StyleToggle";
import { SettingsMenu } from "@/components/SettingsMenu";
import { MobileSettingsSheet } from "@/components/MobileSettingsSheet";
import { Toaster } from "@/components/ui/sonner";

function App() {
  const { isAuthenticated, logout } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem("nyrvex-sidebar-collapsed");
      if (saved !== null) return saved === "1";
    } catch {
      /* fall through to the viewport default */
    }
    // No saved choice: narrow desktop windows (tablets) start with the icon rail.
    return window.innerWidth < 1100;
  });
  const toggleSidebar = () =>
    setSidebarCollapsed((v) => {
      try {
        localStorage.setItem("nyrvex-sidebar-collapsed", v ? "0" : "1");
      } catch {
        /* storage unavailable */
      }
      return !v;
    });
  const [wallpaper, setWallpaperState] = useState<Wallpaper | null>(() => getWallpaper());

  useEffect(() => {
    initializeDarkMode();
  }, []);

  useEffect(() => {
    const handleWallpaperChange = () => setWallpaperState(getWallpaper());
    window.addEventListener("wallpaperchange", handleWallpaperChange);
    window.addEventListener("storage", handleWallpaperChange);
    return () => {
      window.removeEventListener("wallpaperchange", handleWallpaperChange);
      window.removeEventListener("storage", handleWallpaperChange);
    };
  }, []);

  const { tabs, activeTab, onTabChange, visible: showNav, headerAction } =
    useTabNavigationContext();

  // The wall display (/hub) is a full-screen layout of its own: no shell around it.
  const onHub = useRouterState({ select: (s) => s.location.pathname.replace(/\/+$/, "") === "/hub" });
  if (onHub) {
    return (
      <>
        <Outlet />
        <Toaster position="bottom-right" />
      </>
    );
  }

  const wallpaperBackground = wallpaper
    ? wallpaper.type === "preset"
      ? WALLPAPER_PRESETS.find((p) => p.id === wallpaper.value)?.css
      : `url(${wallpaper.value})`
    : undefined;

  return (
    <div
      className={
        wallpaperBackground
          ? "min-h-screen relative transition-colors duration-300 ease-in-out bg-cover bg-center bg-fixed"
          : "min-h-screen relative transition-colors duration-300 ease-in-out"
      }
      style={wallpaperBackground ? { background: wallpaperBackground, backgroundSize: "cover", backgroundPosition: "center", backgroundAttachment: "fixed" } : undefined}
    >
      {/* Semi-transparent overlay so existing card contrast stays legible
          over an arbitrary custom wallpaper, regardless of its own
          brightness/colors. Sits above the wallpaper, below all content.
          Explicit z-0: `position: absolute` elements paint *after* normal
          in-flow content in CSS stacking order regardless of DOM position,
          so without this the overlay was washing out all page content (not
          just the wallpaper) -- the Outlet wrapper below is explicitly
          stacked above it to fix that. */}
      {wallpaperBackground && (
        <div className="absolute inset-0 z-0 pointer-events-none bg-white/40 dark:bg-gray-900/50" />
      )}

      {/* Nav: a floating vertical sidebar on wider screens, and a full-width
          bottom tab bar on phones (flush to the edge, respects the
          home-indicator safe area itself). */}
      {showNav && (
        <>
          <Sidebar
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={onTabChange}
            collapsed={sidebarCollapsed}
            onToggleCollapsed={toggleSidebar}
            footer={
              isAuthenticated ? (
                <>
                  <StyleToggle
                    className={"relative flex w-full items-center gap-3 overflow-hidden whitespace-nowrap px-[22px] py-3 text-left text-[14px] font-medium text-gray-500 outline-none transition-colors hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"}
                    label={<span className={cn("transition-opacity duration-200", sidebarCollapsed && "opacity-0")}>Style</span>}
                  />
                  <SettingsMenu
                    triggerClassName={"relative flex w-full items-center gap-3 overflow-hidden whitespace-nowrap px-[22px] py-3 text-left text-[14px] font-medium text-gray-500 outline-none transition-colors hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"}
                    inlineLabel={<span className={cn("transition-opacity duration-200", sidebarCollapsed && "opacity-0")}>Settings</span>}
                  />
                  <button
                    type="button"
                    onClick={() => logout()}
                    className={"relative flex w-full items-center gap-3 overflow-hidden whitespace-nowrap px-[22px] py-3 text-left text-[14px] font-medium text-gray-500 outline-none transition-colors hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400"}
                    aria-label="Logout"
                  >
                    <LogoutIcon className="h-[19px] w-[19px] flex-none" />
                    <span className={cn("transition-opacity duration-200", sidebarCollapsed && "opacity-0")}>Sign out</span>
                  </button>
                </>
              ) : undefined
            }
          />
          <div className="m-dock-wrap sm:hidden fixed z-30 inset-x-0 bottom-0">
            <TabNavigation
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={onTabChange}
            />
          </div>
        </>
      )}

      {/* Only show controls for authenticated users */}
      {isAuthenticated && (
        <>
          {/* Mobile top bar: brand + section title, time/weather, page action, menu */}
          <div
            className="sm:hidden fixed z-30 inset-x-0 top-0 flex items-center gap-2.5 px-3.5 lg-bar lg-bar--top"
            style={{ paddingTop: "env(safe-area-inset-top)", height: "calc(3.5rem + env(safe-area-inset-top))" }}
          >
            <button
              type="button"
              onClick={() => onTabChange("home")}
              aria-label="Home"
              className="flex-none rounded-lg active:scale-90 transition-transform"
            >
              <BrandMark size={30} />
            </button>
            <h1 className="min-w-0 flex-1 truncate text-[19px] font-semibold tracking-tight text-gray-900 dark:text-white">
              {tabs.find((tt) => tt.id === activeTab)?.label ?? "Home"}
            </h1>
            <TopInfo compact />
            <div className="flex flex-none items-center gap-1.5 [&>button]:flex [&>button]:h-9 [&>button]:w-9 [&>button]:items-center [&>button]:justify-center [&>button]:rounded-lg [&>button]:border [&>button]:border-gray-200 [&>button]:bg-white [&>button]:text-gray-700 [&>button]:transition-transform active:[&>button]:scale-90 dark:[&>button]:border-white/10 dark:[&>button]:bg-white/[0.06] dark:[&>button]:text-gray-200">
              {/* Active page's primary action (Add / Refresh / Edit layout…),
                  published via useMobileHeaderAction. */}
              {headerAction}
              <NotificationBell className="!h-9 !w-9 !rounded-lg" />
              <MobileSettingsSheet
                trigger={
                  <button
                    type="button"
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 transition-transform active:scale-90 dark:border-white/10 dark:bg-white/[0.05] dark:text-gray-200"
                    aria-label="Open menu"
                  >
                    <Bars3Icon className="h-5 w-5" />
                  </button>
                }
              />
            </div>
          </div>
        </>
      )}

      {isAuthenticated && showNav && (
        <CommandPalette tabs={tabs} onTabChange={onTabChange} />
      )}

      <div
        className={
          showNav
            ? cn("relative z-[1] transition-[padding] duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)]", sidebarCollapsed ? "sm:pl-[64px]" : "sm:pl-[220px]")
            : "relative z-[1]"
        }
      >
        {/* Desktop top bar: the active page's header (portalled into the slot)
            shares one row with the clock, so pages start right under it. */}
        {isAuthenticated && showNav && (
          <div className="hidden sm:block mx-auto w-full max-w-[1600px] px-6 pt-3 lg:px-8">
            {/* wraps: on a half-width window the clock drops under the title instead of squeezing it into a sliver */}
            <div className="flex min-h-12 flex-wrap items-center gap-x-4 gap-y-1">
              <div id="page-head-slot" className="min-w-0 flex-1 basis-[420px]" />
              <div className="ml-auto flex flex-none items-center gap-3">
                <NotificationBell />
                <TopInfo />
              </div>
            </div>
          </div>
        )}
        <Outlet />
      </div>
      <Toaster position="bottom-right" />
    </div>
  );
}

export default App;




